import {
  ActionButton,
  buildConfirmOutputDataSchema,
  ConfirmOutputDataType,
  DataVersionStatus,
  Detail,
  Form,
  FormAlerts,
  FormField,
  FormInput,
  FormSelect,
  formatDate,
  GetApiErrorMessage,
  OutputDescriptionProcessingStatesType,
  OutputDescriptionProcessingStateType,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  useAlertContext,
  useConfirmOutputData,
  useMutationErrors,
  useOutputDescriptionProcessingStatesQuery,
  useSystemClassifierItem,
  useSystemClassifierItems,
  useSystemClassifierItemsBy,
  useSystemClassifierSelectOptions,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { FC, useCallback, useMemo } from 'react';
import translations from './OutputDataActionModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ActiveOutputsTable } from './ActiveOutputsTable';
import { useFormContext, useWatch } from 'react-hook-form';
import { OutputDataActionParams } from '../OutputDataDirectory';

const { addTranslations, t, exists } = i18nNsFactory(
  'output-data-action-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export interface ConfirmationModalProps {
  params: OutputDataActionParams;
  onClose: () => void;
  mode?: 'default' | 'edit' | 'calculate';
}

export const ConfirmationModal: FC<ConfirmationModalProps> = (props) => {
  const { params, onClose, mode = 'default' } = props;
  const { hasAccess, ...otherParams } = params;
  const { data, isLoading, refetch } =
    useOutputDescriptionProcessingStatesQuery(otherParams);
  const { mutateAsync } = useConfirmOutputData();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const systemClassifierItemsById = useSystemClassifierItemsBy(
    ['OUTPUT_TYPE', 'FREQUENCY'],
    'id',
  );
  const sourceDataVersionItems = useSystemClassifierItems(
    'SOURCE_DATA_VERSION',
  );
  const confirmationStatusItems = useSystemClassifierItems(
    'CONFIRMATION_STATUS',
  );
  const latestSourceDataVersion = useSystemClassifierItem(
    'SOURCE_DATA_VERSION',
    'LATEST',
  );
  const schema = useMemo(
    () =>
      buildConfirmOutputDataSchema(
        sourceDataVersionItems,
        confirmationStatusItems,
      ),
    [sourceDataVersionItems, confirmationStatusItems],
  );
  const { resetAlert, setSuccess } = useAlertContext();

  if (!data) {
    return null;
  }

  return (
    <Form
      schema={schema}
      defaultValues={getDefaultValues(
        data,
        systemClassifierItemsById['OUTPUT_TYPE'],
        latestSourceDataVersion,
        mode,
      )}
      onSubmit={(data: ConfirmOutputDataType) => {
        resetAlert();
        clearMutationErrors();
        return mutateAsync({ data })
          .then(() => {
            onClose();
            setSuccess(t(getSuccessKeyByMode(mode)));
          })
          .catch((error) => {
            refetch().then(() => {
              setMutationErrors(error?.response?.data?.errors ?? []);
            });
          });
      }}
      serverErrors={mutationErrors}
    >
      <ConfirmationModalInner
        data={data}
        isLoading={isLoading}
        onClose={onClose}
        mode={mode}
        hasGroupAccess={hasAccess}
        systemClassifierItemsById={systemClassifierItemsById}
      />
    </Form>
  );
};

const ConfirmationModalInner: FC<{
  data: OutputDescriptionProcessingStatesType;
  isLoading?: boolean;
  onClose: () => void;
  mode: 'default' | 'edit' | 'calculate';
  hasGroupAccess: boolean;
  systemClassifierItemsById: Record<
    string,
    Map<number, { code: string; name: string }>
  >;
}> = (props) => {
  const {
    data,
    isLoading,
    onClose,
    mode,
    hasGroupAccess,
    systemClassifierItemsById,
  } = props;

  const { setValue } = useFormContext<ConfirmOutputDataType>();
  const selectedOutputIds = useWatch<ConfirmOutputDataType>({
    name: 'outputIds',
  }) as number[];
  const sourceDataVersionOptions = useSystemClassifierSelectOptions([
    'SOURCE_DATA_VERSION',
  ])['SOURCE_DATA_VERSION'];
  const confirmationStatusOptions = useSystemClassifierSelectOptions([
    'CONFIRMATION_STATUS',
  ])['CONFIRMATION_STATUS'];

  const handleSelectedChange = useCallback(
    (selectedOutputIds: number[]) => {
      setValue('outputIds', selectedOutputIds);
    },
    [setValue],
  );

  if (!data) return null;

  return (
    <Modal
      onCancel={onClose}
      width="60rem"
      loading={isLoading}
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton
              type="formSubmit"
              label={t(getButtonKeyByMode(mode))}
              disabled={selectedOutputIds.length === 0}
            />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>
            {t('title', { count: data.outputs.length })}
          </TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="small">
          <Detail label={t('group')}>
            {`${data?.groupCode} - ${data.groupName}`}
          </Detail>
          <Detail label={t('valueDate')}>{formatDate(data.valueDate)}</Detail>
          <Detail label={t('frequency')}>
            {systemClassifierItemsById['FREQUENCY'].get(data.frequencyId)?.name}
          </Detail>
        </Flex>
        {mode === 'calculate' && (
          <Padding top>
            <FormField
              name="sourceDataVersionId"
              label={t('field.versionStatusId.label')}
              style={{ maxWidth: '20rem' }}
              required
            >
              <FormSelect
                name="sourceDataVersionId"
                options={sourceDataVersionOptions}
              />
            </FormField>
          </Padding>
        )}
        <Padding top>
          <Flex gap="small">
            <FormField
              name="statusId"
              label={t('field.confirmationStatusId.label')}
              style={{ width: '15rem' }}
              required
            >
              <FormSelect name="statusId" options={confirmationStatusOptions} />
            </FormField>
            <FormField
              name="comment"
              label={t('field.confirmationComment.label')}
              style={{ flex: 1 }}
            >
              <FormInput name="comment" rows={2} />
            </FormField>
          </Flex>
        </Padding>
        <Padding top>
          <ActiveOutputsTable
            mode="edit"
            data={data}
            hasGroupAccess={hasGroupAccess}
            selectionMode="multiple"
            isSelectable={(output) =>
              isSelectable(output, systemClassifierItemsById['OUTPUT_TYPE'])
            }
            selectedOutputIds={selectedOutputIds}
            onSelectedOutputIdsChange={handleSelectedChange}
          />
        </Padding>
      </Padding>
    </Modal>
  );
};

const getDefaultValues = (
  data: OutputDescriptionProcessingStatesType,
  outputTypes: Map<number, { code: string }>,
  latestSourceDataVersion: { id: number } | undefined,
  mode: 'default' | 'edit' | 'calculate',
) => ({
  groupId: data.groupId,
  comment: '',
  valueDate: data.valueDate,
  outputIds:
    data?.outputs
      .filter((output) => isSelectable(output, outputTypes))
      .filter(({ activeDataObjectVersionStatus }) =>
        mode === 'edit'
          ? isConfirmed(activeDataObjectVersionStatus)
          : !isConfirmed(activeDataObjectVersionStatus),
      )
      .map((output) => output.id) || [],
  sourceDataVersionId: latestSourceDataVersion?.id,
  change: mode === 'edit',
  calculate: mode === 'calculate',
});

const getButtonKeyByMode = (mode: 'default' | 'edit' | 'calculate') => {
  if (mode === 'edit') return 'button.changeConfirmStatus';
  if (mode === 'calculate') return 'button.confirmAndCalculate';
  return 'button.confirm';
};

const getSuccessKeyByMode = (mode: 'default' | 'edit' | 'calculate') => {
  if (mode === 'edit') return 'alert.success.edited';
  if (mode === 'calculate') return 'alert.success.confirmedAndCalculated';
  return 'alert.success.confirmed';
};

const isSelectable = (
  output: OutputDescriptionProcessingStateType,
  outputTypes: Map<number, { code: string }>,
) =>
  outputTypes.get(output.outputTypeId)?.code !== 'INTERMEDIATE' &&
  output.activeDataObjectVersionId !== null;

const isConfirmed = (status: DataVersionStatus | 'MIXED' | null) =>
  status === 'CONFIRMED' ||
  status === 'CONF_PUB' ||
  status === 'CONF_PREPUB' ||
  status === 'CONF_WAIT';
